import { employeeCodeStructureService } from "../../../services/api";

/** Pattern type options for Employee Code settings. */
export const EMPLOYEE_CODE_PATTERN_OPTIONS = [
  {
    value: "manual",
    label: "Manual",
    description: "HR assigns codes during onboarding",
  },
  {
    value: "automated",
    label: "Automated",
    description: "System generates the next code automatically",
  },
];

/** Organization level options for automated codes (company save supported). */
export const EMPLOYEE_CODE_ORG_LEVEL_OPTIONS = [
  {
    value: "company",
    label: "Company",
    description: "Single pattern for all employees",
  },
  {
    value: "branch",
    label: "Branch",
    description: "Different pattern per branch (preview)",
  },
  {
    value: "department",
    label: "Department",
    description: "Different pattern per department (preview)",
  },
];

/** Today's date in IST (YYYY-MM-DD). */
export function getEmployeeCodeTodayDate() {
  const now = new Date();
  const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return istTime.toISOString().split("T")[0];
}

/** Tomorrow's date in IST (YYYY-MM-DD). */
export function getEmployeeCodeTomorrowDate() {
  const today = new Date(getEmployeeCodeTodayDate());
  today.setDate(today.getDate() + 1);
  return today.toISOString().split("T")[0];
}

/**
 * Validate and save a manual employee-code pattern.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export async function saveManualEmployeeCodePattern(effectiveDate) {
  if (!effectiveDate) {
    return { ok: false, message: "Please select an effective date for manual pattern" };
  }

  const selectedDate = new Date(effectiveDate);
  const today = new Date(getEmployeeCodeTodayDate());
  if (selectedDate < today) {
    return {
      ok: false,
      message: "Effective date cannot be in the past. It must be today or in the future.",
    };
  }

  try {
    await employeeCodeStructureService.saveStructure({
      patternType: "manual",
      effectiveDate,
    });
    return { ok: true };
  } catch (error) {
    const apiMessage = error?.response?.data?.message;
    if (
      error?.response?.status === 400 &&
      typeof apiMessage === "string" &&
      apiMessage.includes("same configuration consecutively")
    ) {
      return { ok: false, message: apiMessage };
    }
    return { ok: false, message: "Failed to save manual pattern" };
  }
}

/**
 * Load current structure pattern type + manual effective date.
 */
export async function loadEmployeeCodePatternState() {
  try {
    const data = await employeeCodeStructureService.getCurrentStructure();
    const structure = data?.data;
    if (!structure) {
      return { patternType: "manual", manualEffectiveDate: getEmployeeCodeTodayDate() };
    }
    return {
      patternType: structure.patternType || "manual",
      manualEffectiveDate:
        structure.patternType === "manual" && structure.effectiveDate
          ? structure.effectiveDate
          : getEmployeeCodeTodayDate(),
    };
  } catch (error) {
    if (error?.response?.status === 404) {
      return { patternType: "manual", manualEffectiveDate: getEmployeeCodeTodayDate() };
    }
    console.error("Error loading pattern type:", error);
    return { patternType: "manual", manualEffectiveDate: getEmployeeCodeTodayDate() };
  }
}
